import axios from "axios";
import { sortedByKey } from "src/util/util.sortFilterOptions";
import { IListOption } from "../models/listOption.models";
import { UserRole } from "../models/user.model";
import { FULFILLED, PENDING, REJECTED } from "./action-type.util";

const GME = 1;
const GSI = 2;

const ACTION_TYPES = {
  FETCH_OPTIONS: "applications/FETCH_OPTIONS",
  FETCH_APPLICATION: "applications/FETCH_APPLICATION",
  SHOULD_SAVE: "applicationBuilder/SHOULD_SAVE",
  STATUS_HISTORY: "applicationBuilder/STATUS_HISTORY",
};

const initialState = {
  loading: false,
  shouldSave: false,
  newAppId: null,
  uwSpecialistID: null,
  selectStateList: [],
  selectUserList: [],
  selectUWList: [],
  selectGenderList: [],
  selectHabitList: [],
  selectBranchList: [],
  selectCarrierTypeList: [],
  selectStatusList: [],
  selectProductList: [],
  selectProductTypeList: [],
  selectGmeGsiList: [],
};

export type ApplicationBuilderState = Readonly<typeof initialState>;

/**
 * ApplicationBuilder Reducer
 */
export default (state: ApplicationBuilderState = initialState, action): ApplicationBuilderState => {
  switch (action.type) {
    case PENDING(ACTION_TYPES.FETCH_OPTIONS):
    case PENDING(ACTION_TYPES.FETCH_APPLICATION):
      return {
        ...state,
        loading: true,
      };

    case FULFILLED(ACTION_TYPES.FETCH_OPTIONS):
      const userList = action.payload.data.selectUserList.map(({ id, fullName, roleTypeID }) => ({
        key: fullName,
        value: id,
        roleTypeID,
      }));

      const selectGmeGsiList = action.payload.data.selectGMEGSIList.map(({ gmegsiId, gmegsiDesc, gmegsiType }) => ({
        key: gmegsiType === GME ? `GME:  ${gmegsiDesc}` : `GSI: ${gmegsiDesc}`,
        value: `${gmegsiType}:${gmegsiId}`,
        gmegsiType,
      }));

      return {
        ...state,
        loading: false,
        ...action.payload.data,
        selectProductList: remapProductList(action.payload.data),
        selectCarrierTypeList: action.payload.data.selectCarrierTypeList.sort(sortedByKey),
        selectBranchList: action.payload.data.selectBranchList.sort(sortedByKey),
        selectStateList: action.payload.data.selectStateList.sort(sortedByKey),
        selectUserList: userList.sort(sortedByKey),
        selectUWList: userList.filter(user => user.roleTypeID === UserRole.UnderwritingAssociate).sort(sortedByKey),
        selectGmeGsiList,
      };

    case FULFILLED(ACTION_TYPES.FETCH_APPLICATION):
      return {
        ...state,
        loading: false,
        ...action.payload.data,
      };

    case REJECTED(ACTION_TYPES.FETCH_OPTIONS):
    case REJECTED(ACTION_TYPES.FETCH_APPLICATION):
      return {
        ...state,
        loading: false,
      };

    case ACTION_TYPES.SHOULD_SAVE:
      return {
        ...state,
        shouldSave: action.payload.shouldSave,
        newAppId: action.payload.id,
      };

    default:
      return state;
  }
};

/**
 * Actions for fetching data
 */
export const fetchApplicationOptions = () => ({
  type: ACTION_TYPES.FETCH_OPTIONS,
  payload: axios.get("applications/new"),
});

export const fetchApplicationById = (appId: number) => ({
  type: ACTION_TYPES.FETCH_APPLICATION,
  payload: axios.get(`applications/${appId}`),
});

export const fetchStatusHistory = (appId: string) => ({
  type: ACTION_TYPES.STATUS_HISTORY,
  payload: axios.get(`applications/${appId}/statusHistory`),
});

// Tracking if app should be saved
export const appBuilderShouldSave = (shouldSave: boolean, id = null) => ({
  type: ACTION_TYPES.SHOULD_SAVE,
  payload: { shouldSave, id },
});

/**
 * Helpers
 */
function remapProductList(data: any): IListOption[] {
  return data.selectProductTypeList
    .map(plan => ({
      key: plan.planTypeDesc,
      value: plan.planID,
      carrierID: plan.carrierID,
      typeDesc: plan.typeDesc,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
}
